<template>
    <div class="file-upload">
        <!-- URL输入方式 -->
        <div v-if="mode === 'url'" class="url-input">
            <a-input v-model:value="urlValue" :placeholder="placeholder" @change="handleUrlChange">
                <template #suffix>
                    <a-button type="link" @click="switchMode('upload')" size="small">
                        <UploadOutlined />
                        上传
                    </a-button>
                </template>
            </a-input>
        </div>

        <!-- 文件上传方式 -->
        <div v-else class="upload-area">
            <a-upload :file-list="fileList" :before-upload="beforeUpload" :show-upload-list="false" accept="image/*"
                @change="handleFileChange">
                <div class="upload-trigger">
                    <div v-if="!imageUrl && !uploading" class="upload-placeholder">
                        <PlusOutlined />
                        <div class="upload-text">点击上传图片</div>
                    </div>
                    <div v-else-if="uploading" class="upload-loading">
                        <a-spin />
                        <div class="upload-text">上传中...</div>
                        <a-progress :percent="uploadProgress" size="small" :show-info="false" />
                    </div>
                    <div v-else class="image-preview">
                        <img :src="imageUrl" alt="预览" />
                        <div class="image-overlay">
                            <a-button type="text" @click.stop="removeImage">
                                <DeleteOutlined />
                            </a-button>
                        </div>
                    </div>
                </div>
            </a-upload>

            <div class="upload-actions">
                <a-button type="link" @click="switchMode('url')" size="small">
                    <LinkOutlined />
                    输入URL
                </a-button>
            </div>
        </div>

        <!-- 图片预览（URL模式） -->
        <div v-if="mode === 'url' && urlValue" class="url-preview">
            <img :src="urlValue" alt="URL预览" @error="handleImageError" />
        </div>
    </div>
</template>

<script>
import { ref, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import {
    Upload, Input, Button, Spin, Progress
} from 'ant-design-vue'
import {
    UploadOutlined, PlusOutlined, DeleteOutlined, LinkOutlined
} from '@ant-design/icons-vue'
import fileUploadService from '../services/fileUploadService'

export default {
    name: 'FileUpload',
    components: {
        'a-upload': Upload,
        'a-input': Input,
        'a-button': Button,
        'a-spin': Spin,
        'a-progress': Progress,
        UploadOutlined, PlusOutlined, DeleteOutlined, LinkOutlined
    },
    props: {
        // 当前值
        value: {
            type: String,
            default: ''
        },
        // 文件分类
        category: {
            type: String,
            default: 'articleImages',
            validator: (value) => ['avatar', 'articleImages', 'articleFiles', 'siteBackground'].includes(value)
        },
        // 占位符文本
        placeholder: {
            type: String,
            default: '请输入图片URL或点击上传'
        },
        // 默认模式
        defaultMode: {
            type: String,
            default: 'url',
            validator: (value) => ['url', 'upload'].includes(value)
        }
    },
    emits: ['update:value', 'change'],
    setup(props, { emit }) {
        const mode = ref(props.defaultMode)
        const urlValue = ref('')
        const imageUrl = ref('')
        const uploading = ref(false)
        const uploadProgress = ref(0)
        const fileList = ref([])

        // 初始化值
        if (props.value) {
            if (props.defaultMode === 'url') {
                urlValue.value = props.value
            } else {
                imageUrl.value = props.value
            }
        }

        // 监听外部值变化
        watch(() => props.value, (newValue) => {
            if (mode.value === 'url') {
                urlValue.value = newValue || ''
            } else {
                imageUrl.value = newValue || ''
            }
        })

        // 切换模式
        const switchMode = (newMode) => {
            mode.value = newMode
            if (newMode === 'url') {
                urlValue.value = imageUrl.value
                imageUrl.value = ''
            } else {
                imageUrl.value = urlValue.value
                urlValue.value = ''
            }
        }

        // URL输入变化
        const handleUrlChange = () => {
            emit('update:value', urlValue.value)
            emit('change', urlValue.value)
        }

        // 图片加载错误
        const handleImageError = () => {
            message.error('图片加载失败，请检查URL是否正确')
        }

        // 上传前验证
        const beforeUpload = (file) => {
            // 阻止默认上传行为，我们手动处理
            return false
        }

        // 文件选择变化
        const handleFileChange = async (info) => {
            const { file } = info

            if (file.status === 'removed') {
                return
            }

            try {
                uploading.value = true
                uploadProgress.value = 0

                // 使用通用上传服务
                const result = await fileUploadService.uploadFile(
                    file,
                    props.category,
                    (progress) => {
                        uploadProgress.value = progress || 0
                    }
                )

                if (result.success) {
                    imageUrl.value = result.url
                    emit('update:value', result.url)
                    emit('change', result.url)
                    message.success('上传成功')
                } else {
                    throw new Error(result.error || '上传失败')
                }

            } catch (error) {
                console.error('上传失败:', error)
                message.error(error.message || '上传失败')
            } finally {
                uploading.value = false
                uploadProgress.value = 0
            }
        }

        // 移除图片
        const removeImage = () => {
            imageUrl.value = ''
            fileList.value = []
            emit('update:value', '')
            emit('change', '')
        }

        return {
            mode,
            urlValue,
            imageUrl,
            uploading,
            uploadProgress,
            fileList,
            switchMode,
            handleUrlChange,
            handleImageError,
            beforeUpload,
            handleFileChange,
            removeImage
        }
    }
}
</script>

<style scoped>
.file-upload {
    width: 100%;
}

.url-input {
    width: 100%;
}

.upload-area {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.upload-trigger {
    width: 200px;
    height: 120px;
    border: 2px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.3s;
    overflow: hidden;
    position: relative;
}

.upload-trigger:hover {
    border-color: #1890ff;
}

.upload-placeholder,
.upload-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
}

.upload-text {
    margin-top: 8px;
    font-size: 14px;
}

.image-preview {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
}

.image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
}

.image-preview:hover .image-overlay {
    opacity: 1;
}

.image-overlay .ant-btn {
    color: white;
    border: none;
}

.upload-actions {
    text-align: center;
}

.url-preview {
    margin-top: 12px;
    max-width: 200px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #f0f0f0;
}

.url-preview img {
    width: 100%;
    height: auto;
    max-height: 120px;
    object-fit: cover;
}
</style>